import { useState } from 'react'

import { fetchAcfOptions, fetchItems, fetchSearch, fetchSingle, fetchSingleById } from '@/api'

export default function useStore() {
  const { state } = window.__REACT_WORDPRESS__
  const [appState, setAppState] = useState(state)

  const menu = ({ name }) => {
    return appState.menus[name]
  }

  const request = ({ type, params }) => {
    return appState[type].requests.find(async (req) => {
      if (Object.keys(req.params).length === Object.keys(params).length) {
        const keyArray = Object.keys(req.params)
        const response = keyArray.map((key) => {
          if (req.params[key] === params[key]) return req.params[key]
        })
        return response
      }
    })
  }

  const getCurrentSingleBySlug = ({ type, slug }) => {
    for (const id in appState[type]) {
      if (appState[type][id] && decodeURI(appState[type][id].slug) === slug) {
        return appState[type][id]
      } else return false
    }
  }

  const getPosts = ({ type }) => {
    return appState[type]
  }

  const getTotalPages = ({ type, params }) => {
    const dataRequest = request({ type, params })
    return dataRequest ? dataRequest.totalPages : 0
  }

  const requestedItems = ({ type, params }) => {
    const dataRequest = request({ type, params })
    return dataRequest ? dataRequest.data.map((id) => appState[type][id]) : []
  }

  const singleById = ({ type, id }) => {
    return appState[type][id]
  }

  const searchTerm = () => {
    return appState.search_term
  }

  const acfOptions = () => {
    return appState.acf_options
  }

  const addItem = ({ type, item }) => {
    if (
      item &&
      type &&
      !appState[type].hasOwnProperty(item.id) // eslint-disable-line
    ) {
      appState[type][item.id] = item
      setAppState({ ...appState })
    }
  }

  const addRequest = ({ type, request }) => {
    const newState = appState
    newState[type].requests.push(request)
    setAppState({ ...newState })
  }

  const setLoading = (loading) => {
    const newState = appState
    newState.site.loading = loading
    setAppState({ ...newState })
  }

  const setIntro = (introShown) => {
    const newState = appState
    newState.site.introShown = introShown
    setAppState({ ...newState })
  }

  const setDocTitle = (title) => {
    const newState = appState
    newState.site.docTitle = title
    setAppState({ ...newState })
  }

  const setSearchTerm = (searchTerm) => {
    const newState = appState
    newState.search_term = searchTerm
    setAppState({ ...newState })
  }

  const setAcfOptions = (options) => {
    const newState = appState
    newState.acf_options = options
    setAppState({ ...newState })
  }

  const getSingleBySlug = ({ type, slug, showLoading = false }) => {
    if (!getCurrentSingleBySlug({ type, slug })) {
      if (showLoading) {
        setLoading(true)
      }
      return fetchSingle({ type, params: { slug } }).then(({ data: [item] }) => {
        addItem({ type, item })
        if (showLoading) {
          setLoading(false)
        }
        return item
      })
    } else {
      return Promise.resolve()
    }
  }

  const getSingleById = ({ type, id, embed = false, showLoading = false, batch = false }) => {
    if (!singleById({ type, id })) {
      if (showLoading) {
        setLoading(true)
      }
      return fetchSingleById({ type, id, batch, embed }).then(({ data }) => {
        if (batch) {
          data.forEach((item) => addItem({ type, item }))
        } else {
          addItem({ type, item: data })
        }
        if (showLoading) {
          setLoading(false)
        }
      })
    } else {
      return Promise.resolve()
    }
  }

  const getItems = async ({ type, params, showLoading = false }) => {
    if (!request({ type, params })) {
      if (showLoading) {
        setLoading(true)
      }
      return fetchItems({ type, params }).then(
        ({ data: items, headers: { 'x-wp-total': total, 'x-wp-totalpages': totalPages } }) => {
          items.forEach((item) => addItem({ type, item }))
          addRequest({
            type,
            request: {
              params,
              total: parseInt(total),
              totalPages: parseInt(totalPages),
              data: items.map((i) => i.id),
            },
          })
          if (showLoading) {
            setLoading(false)
          }
        },
      )
    } else {
      return Promise.resolve()
    }
  }

  const updateDocTitle = ({ parts = [], sep = ' – ' }) => {
    parts[0] ? setDocTitle(parts.join(sep)) : setDocTitle(parts[1])
    document.title = appState.site.docTitle
  }

  const getAcfOptions = () => {
    return fetchAcfOptions().then((response) => {
      return response.data.acf
    })
  }

  const performSearch = ({ term, params }) => {
    return fetchSearch({ term, params })
      .then((response) => {
        return response
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error({ err: err }, `No results for ${term}.`)
        throw err
      })
  }

  return {
    appState,
    setAppState,
    menu,
    request,
    getCurrentSingleBySlug,
    getPosts,
    getTotalPages,
    requestedItems,
    singleById,
    searchTerm,
    acfOptions,
    setIntro,
    setDocTitle,
    setSearchTerm,
    setAcfOptions,
    getSingleBySlug,
    getSingleById,
    getItems,
    updateDocTitle,
    getAcfOptions,
    performSearch,
  }
}
